/**
 * Store de autenticación con Zustand
 * Sprint 3 - Maneja login con validación de dispositivo
 * Sprint 5 - Maneja estado de cuenta no verificada
 */

import { create } from 'zustand';
import { TOKEN_STORAGE_KEY } from '../config';
import { authService, deviceService } from '../services';
import { useDeviceStore } from './deviceStore';
import { useBiometricStore } from './biometricStore';
import { useManualLocationStore } from './manualLocationStore';
import { useProviderContextStore } from './providerContextStore';
import type {
    User,
    LoginCredentials,
    RegisterData,
    ApiError,
    DeviceApiError,
} from '../types';
import { isDeviceBindRequired, isDeviceMismatch, isDeviceAlreadyBound, isAccountNotVerified } from '../types';

const MODE_ONBOARDED_STORAGE_KEY = 'marketplace_mode_onboarded';
export const APP_HAS_LOGGED_IN_ONCE_KEY = 'marketplace_has_logged_in_once';

async function syncProviderContextsForUser(user: User): Promise<void> {
    if (user.operation_mode === 'operator') {
        await useProviderContextStore.getState().loadContexts();
        return;
    }

    useProviderContextStore.getState().clearContext();
}

// Estados especiales del flujo de login con dispositivo
export type LoginDeviceState =
    | 'idle'
    | 'authenticating'
    | 'bind_required'
    | 'binding'
    | 'device_mismatch'
    | 'device_already_bound'
    | 'account_not_verified' // Sprint 5
    | 'success'
    | 'error';

interface AuthState {
    // Estado
    user: User | null;
    token: string | null;
    isLoading: boolean;
    isInitialized: boolean;
    error: ApiError | DeviceApiError | null;

    // Sprint 7: Computed helper
    isAdmin: boolean;

    // Sprint 3: Estado del flujo de login con dispositivo
    // Sprint 5: También maneja account_not_verified
    loginDeviceState: LoginDeviceState;
    bindNonce: string | null;
    pendingCredentials: LoginCredentials | null;

    // Acciones
    login: (credentials: LoginCredentials) => Promise<boolean>;
    completeBind: () => Promise<boolean>;
    register: (data: RegisterData) => Promise<boolean>;
    logout: () => Promise<void>;
    checkAuth: () => Promise<void>;
    updateUser: (patch: Partial<User>) => void;
    clearError: () => void;
    resetLoginState: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
    // Estado inicial
    user: null,
    token: localStorage.getItem(TOKEN_STORAGE_KEY),
    isLoading: false,
    isInitialized: false,
    error: null,

    // Sprint 7: Computed - se actualiza cuando cambia user
    get isAdmin() {
        return get().user?.is_admin === true;
    },

    // Sprint 3
    loginDeviceState: 'idle',
    bindNonce: null,
    pendingCredentials: null,

    // Login con validación de dispositivo
    login: async (credentials: LoginCredentials): Promise<boolean> => {
        set({
            isLoading: true,
            error: null,
            loginDeviceState: 'authenticating',
            pendingCredentials: credentials,
        });

        try {
            // Intentar login con datos de dispositivo
            const response = await deviceService.loginWithDevice(
                credentials.email,
                credentials.password
            );

            // Login exitoso - usuario con dispositivo vinculado
            const { user, device, token } = response.data;

            // Guardar token
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
            localStorage.setItem(APP_HAS_LOGGED_IN_ONCE_KEY, '1');
            // Forzar selección de modo en cada inicio de sesión.
            localStorage.removeItem(MODE_ONBOARDED_STORAGE_KEY);

            // Actualizar store de dispositivo
            useDeviceStore.getState().setDevice(device);

            // Sprint 7: Actualizar token biométrico si está habilitado
            useBiometricStore.getState().updateToken(token);
            await syncProviderContextsForUser(user);

            set({
                user,
                token,
                isLoading: false,
                error: null,
                loginDeviceState: 'success',
                pendingCredentials: null,
                bindNonce: null,
            });

            return true;
        } catch (err) {
            const error = err as DeviceApiError;

            console.log('[authStore.login] Error caught:', {
                error_code: error.error_code,
                message: error.message,
                hasBindNonce: !!error.data?.bind_nonce,
                isBindRequired: isDeviceBindRequired(error),
                isMismatch: isDeviceMismatch(error),
            });

            // Caso especial: requiere bind de dispositivo
            if (isDeviceBindRequired(error)) {
                set({
                    isLoading: false,
                    error: null,
                    loginDeviceState: 'bind_required',
                    bindNonce: error.data?.bind_nonce || null,
                    // Mantener pendingCredentials para el bind
                });
                return false;
            }

            // Caso especial: dispositivo no coincide
            if (isDeviceMismatch(error)) {
                set({
                    isLoading: false,
                    error,
                    loginDeviceState: 'device_mismatch',
                    pendingCredentials: null,
                    bindNonce: null,
                });
                return false;
            }

            // Sprint 5: Caso especial - cuenta no verificada
            if (isAccountNotVerified(error)) {
                set({
                    isLoading: false,
                    error,
                    loginDeviceState: 'account_not_verified',
                    // Mantener pendingCredentials para redirigir a verificación
                });
                return false;
            }

            // Otros errores
            set({
                isLoading: false,
                error: error.message ? error : { message: 'Error al iniciar sesión' },
                loginDeviceState: 'error',
            });
            return false;
        }
    },

    // Completar el bind cuando se requiere
    completeBind: async (): Promise<boolean> => {
        const { pendingCredentials, bindNonce } = get();

        if (!pendingCredentials || !bindNonce) {
            set({
                error: { message: 'No hay datos pendientes para vincular dispositivo' },
                loginDeviceState: 'error',
            });
            return false;
        }

        set({ isLoading: true, loginDeviceState: 'binding' });

        try {
            const response = await deviceService.bindDevice(
                pendingCredentials.email,
                pendingCredentials.password,
                bindNonce
            );

            const { device, token } = response.data;

            // Guardar token
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
            localStorage.setItem(APP_HAS_LOGGED_IN_ONCE_KEY, '1');
            // Forzar selección de modo en cada inicio de sesión.
            localStorage.removeItem(MODE_ONBOARDED_STORAGE_KEY);

            // Actualizar store de dispositivo
            useDeviceStore.getState().setDevice(device);

            // Obtener datos del usuario
            const userResponse = await authService.me();
            await syncProviderContextsForUser(userResponse.data.user);

            set({
                user: userResponse.data.user,
                token,
                isLoading: false,
                error: null,
                loginDeviceState: 'success',
                pendingCredentials: null,
                bindNonce: null,
            });

            return true;
        } catch (err) {
            const error = err as DeviceApiError;

            // Caso especial: dispositivo ya vinculado a otra cuenta
            if (isDeviceAlreadyBound(error)) {
                set({
                    isLoading: false,
                    error,
                    loginDeviceState: 'device_already_bound',
                });
                return false;
            }

            // Otros errores
            set({
                isLoading: false,
                error: error.message ? error : { message: 'Error al vincular dispositivo' },
                loginDeviceState: 'error',
            });
            return false;
        }
    },

    // Register - no genera token, solo crea usuario
    register: async (data: RegisterData): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
            await authService.register(data);

            set({
                isLoading: false,
                error: null,
            });

            // Registro exitoso - el usuario debe hacer login para vincular dispositivo
            return true;
        } catch (err) {
            const error = err as ApiError;
            set({
                isLoading: false,
                error: error.message ? error : { message: 'Error al registrar usuario' },
            });
            return false;
        }
    },

    // Logout
    logout: async (): Promise<void> => {
        const { token } = get();

        // Limpiar estado local primero
        localStorage.removeItem(TOKEN_STORAGE_KEY);

        // Limpiar store de dispositivo
        useDeviceStore.getState().clearDevice();
        useManualLocationStore.getState().reset();
        useProviderContextStore.getState().clearContext();

        set({
            user: null,
            token: null,
            error: null,
            loginDeviceState: 'idle',
            pendingCredentials: null,
            bindNonce: null,
        });

        // Intentar invalidar token en servidor
        if (token) {
            try {
                await authService.logout();
            } catch {
                // Ignorar errores
            }
        }
    },

    // Verificar autenticación al iniciar
    checkAuth: async (): Promise<void> => {
        const { token } = get();

        if (!token) {
            set({ isInitialized: true });
            return;
        }

        set({ isLoading: true });

        try {
            const response = await authService.me();

            // Obtener info del dispositivo
            await useDeviceStore.getState().fetchDevice();
            await syncProviderContextsForUser(response.data.user);

            set({
                user: response.data.user,
                isLoading: false,
                isInitialized: true,
            });
            localStorage.setItem(APP_HAS_LOGGED_IN_ONCE_KEY, '1');
        } catch {
            // Token inválido
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            useDeviceStore.getState().clearDevice();
            useProviderContextStore.getState().clearContext();
            set({
                user: null,
                token: null,
                isLoading: false,
                isInitialized: true,
            });
        }
    },

    updateUser: (patch): void => {
        set((state) => ({
            user: state.user ? { ...state.user, ...patch } : null,
        }));
    },

    // Limpiar error
    clearError: (): void => {
        set({ error: null });
    },

    // Resetear estado de login
    resetLoginState: (): void => {
        set({
            loginDeviceState: 'idle',
            bindNonce: null,
            pendingCredentials: null,
            error: null,
        });
    },
}));
